import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import InternalHeader from 'components/Navigation/InternalHeader';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import BaseInput from 'components/Inputs/BaseInput';
import { showErrorToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';
import Modal from 'components/Modal';

import { decimalOnly, numberWithCommas } from 'utils/inputValidation';
import { APP_NAME, CARDS, ERROR_MESSAGES, REQUIRED_RULE } from 'utils/constants';
import { BUTTON_COLORS, OPERATION_KINDS_AUX } from 'utils/enums';
import { checkFileType, validateFile } from 'utils/fileUtils';
import { useCountdown } from 'hooks/useCountdown';
import { IOperation } from 'utils/interfaces';
import GeneralApi from 'utils/generalApi';
import { addMinutes } from 'date-fns/esm';
import { useAuth } from 'contexts/Auth';

export default function ConfirmOperationPage() {
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      type: 'mail',
      amount: '0',
      amount_aux: '0',
    },
  });
  const history = useNavigate();
  const auth = useAuth();
  let { id: operationId } = useParams();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const evidenceType = watch('type', 'mail');
  const promoCode = watch('promoCode');
  const amount = watch('amount');

  const promoTimer = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [operation, setOperation] = useState<IOperation | undefined>();
  const [showResultModal, setShowResultModal] = useState(false);

  const [appliedPromo, setAppliedPromo] = useState();
  const [currentBid, setCurrentBid] = useState(0);
  const [currentOffer, setCurrentOffer] = useState(0);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null | undefined>(null);
  const [ranges, setRanges] = useState<
    Array<{
      importe: number;
      BID: number;
      OFFER: number;
    }>
  >([]);

  const [minutes, seconds] = useCountdown(
    addMinutes(new Date(operation?.reactivated_at || operation?.created_at || ''), 10)
  );

  useEffect(() => {
    const getCurrentChange = async () => {
      const result = await generalApi.get(`/current_change`);
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      setCurrentBid(result.data.BID);
      setCurrentOffer(result.data.OFFER);
      setRanges(result.data.ranges);
    };
    const applyExceptions = async (operationData?: IOperation) => {
      const result = await generalApi.post(`/get_bid_and_offer`, {
        base: {
          change: operationData?.change,
          sp_compra: operationData?.sp_compra,
          sp_venta: operationData?.sp_venta,
        },
        ss: operationData?.special_service
          ? {
              sp_compra: operationData?.special_service?.sp_compra,
              sp_venta: operationData?.special_service?.sp_venta,
            }
          : null,
        promo: appliedPromo,
      });
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      setCurrentBid(result.data.BID);
      setCurrentOffer(result.data.OFFER);
    };
    const getOperation = async () => {
      setLoading(true);
      const result = await generalApi.get(`/operation/${operationId}`);
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setOperation(result.data);
      setRanges(result.data.ranges);
      if (result.data?.special_service) applyExceptions(result.data);
      else getCurrentChange();
      setLoading(false);
    };
    if (operationId && !operation) getOperation();
    else if (appliedPromo) applyExceptions(operation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalApi, operationId, operation, appliedPromo]);

  useEffect(() => {
    const getCurrentChange = async () => {
      const result = await generalApi.get(`/current_change`);
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      setCurrentBid(result.data.BID);
      setCurrentOffer(result.data.OFFER);
      setRanges(result.data.ranges);
      updateFromLeft(amount, operation?.action);
    };
    const getPromo = async () => {
      const result = await generalApi.post('/check_promo', {
        promoCode,
      });
      if (!result.success || (appliedPromo && !result.data)) {
        setAppliedPromo(undefined);
        getCurrentChange();
      } else if (!appliedPromo && result.data) {
        setAppliedPromo(result.data);
        updateFromLeft(amount, operation?.action);
      }
    };
    if (promoTimer.current) clearTimeout(promoTimer.current);
    promoTimer.current = setTimeout(() => {
      if (promoCode) getPromo();
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalApi, promoCode]);

  const submitAction = async (data: any) => {
    if (minutes + seconds <= 0)
      return showErrorToast(
        'El plazo para esta transacción ha caducado. Comuníquese con nosotros para reactivar la operación'
      );
    if (data.type === 'file' && !documentFile) return showErrorToast('Debe seleccionar un archivo');

    let finalData = new FormData();
    finalData.append('operationId', operationId as string);
    finalData.append('action', operation?.action || 'V');
    finalData.append('amount', data.amount);
    finalData.append('type', data.type);
    if (appliedPromo && data.promoCode) finalData.append('promoCode', data.promoCode);
    if (documentFile) {
      finalData.append('file_type', checkFileType(documentFile.name));
      finalData.append('file', documentFile);
    }

    setLoading(true);
    let result: any = await generalApi.post('/confirm_operation', finalData);
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    setShowResultModal(true);
  };

  const updateFromLeft = useCallback(
    (value: string, action?: OPERATION_KINDS_AUX) => {
      if (!ranges || !value || !action) return;
      let bid = currentBid,
        offer = currentOffer;
      if (!appliedPromo && !operation?.special_service) {
        const currentRange = ranges.reduce(
          (a, b) => (b.importe < parseFloat(value) && b.importe > a.importe ? b : a),
          { importe: 0, BID: currentBid, OFFER: currentOffer }
        );
        bid = currentRange.BID;
        offer = currentRange.OFFER;
        setCurrentBid(bid);
        setCurrentOffer(offer);
      }
      if (action === OPERATION_KINDS_AUX.V) {
        setValue('amount_aux', numberWithCommas((Number(value) * bid)?.toFixed(2)));
      } else if (action === OPERATION_KINDS_AUX.C) {
        setValue('amount_aux', numberWithCommas((Number(value) * offer)?.toFixed(2)));
      }
    },
    [ranges, setValue, currentBid, currentOffer, appliedPromo, operation]
  );

  const updateFromRight = useCallback(
    (
      value: string,
      action?: OPERATION_KINDS_AUX,
      newRanges?: Array<{
        importe: number;
        BID: number;
        OFFER: number;
      }>
    ) => {
      if (!newRanges || !value || !action) return;
      const rangeSoles = (amount: number) =>
        action === OPERATION_KINDS_AUX.V
          ? newRanges
              .map((c) => ({
                importe: c.importe * c.BID,
                BID: c.BID,
                OFFER: c.OFFER,
              }))
              .reduce((a, b) => (b.importe < amount && b.importe > a.importe ? b : a), {
                importe: 0,
                BID: currentBid,
                OFFER: currentOffer,
              })
          : newRanges
              .map((c) => ({
                importe: c.importe * c.OFFER,
                BID: c.BID,
                OFFER: c.OFFER,
              }))
              .reduce((a, b) => (b.importe < amount && b.importe > a.importe ? b : a), {
                importe: 0,
                BID: currentBid,
                OFFER: currentOffer,
              });

      const values = rangeSoles(Number(value));
      if (appliedPromo || operation?.special_service) {
        values.BID = currentBid;
        values.OFFER = currentOffer;
      }
      if (currentBid !== values.BID) setCurrentBid(values.BID);
      if (currentOffer !== values.OFFER) setCurrentOffer(values.OFFER);
      if (action === OPERATION_KINDS_AUX.V) {
        setValue(
          'amount',
          currentOffer > 0 ? numberWithCommas((Number(value) / values.BID)?.toFixed(2)) : '0'
        );
      } else if (action === OPERATION_KINDS_AUX.C) {
        setValue('amount', numberWithCommas((Number(value) / values.OFFER)?.toFixed(2)));
      }
    },
    [currentBid, currentOffer, setValue, appliedPromo, operation]
  );

  const checkFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let isValid = validateFile(event.target, 'document');
    if (!isValid || !event.target?.files) {
      setDocumentFile(null);
      showErrorToast('Formato de documento no válido, debe ser un archivo .png, .jpg o .jpeg');
      return;
    }
    setDocumentFile(event.target.files[0]);
    if (checkFileType(event.target.files[0].name) === 'image') {
      var reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        setImagePreview(e?.target?.result);
      };
      await reader.readAsDataURL(event.target.files[0]);
    }
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        errors={errors}
        watch={watch}
        control={control}
        rules={rules}
        {...props}
      />
    );
  };
  return (
    <PrivateRoute>
      <div className="min-h-full bg-gray-100 text-[#1B205A]">
        <Modal isOpen={showResultModal} onClose={() => history('/my_operations')}>
          <div className="bg-primary-600 rounded-t-lg px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                Operación confirmada
              </h3>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <p>Vamos a revisar tu constancia, validar tus datos y procesar tu operación.</p>
            <p>Cualquier duda escríbenos a nuestra linea whatsapp: 987654321</p>
          </div>
          <div className="bg-gray-50 rounded-b-lg px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <SolidButton className="w-auto sm:ml-3" onClick={() => history('/my_operations')}>
              Aceptar
            </SolidButton>
          </div>
        </Modal>
        <InternalHeader />
        <div className="mx-auto max-w-4xl pb-8 pt-32 sm:pt-0 2xl:px-24">
          <div className="justify-between space-y-4 px-8 pt-8 pb-4 sm:flex sm:space-y-0 sm:px-0">
            <p className="text-2xl font-bold">
              {operation?.action === OPERATION_KINDS_AUX.V
                ? 'Venta de dólares'
                : 'Compra de dólares'}
            </p>
          </div>
          <form onSubmit={handleSubmit(submitAction)} className="bg-white shadow sm:rounded-lg">
            <div className="divide-y divide-gray-200">
              <div className="space-y-6 px-4 pt-6 pb-8 lg:px-12">
                <div className="text-center text-xl font-bold">
                  {!isNaN(minutes) && !isNaN(seconds) && (
                    <p>
                      {minutes + seconds > 0
                        ? `Tiempo restante: ${String(minutes).padStart(2, '0')}:${String(
                            seconds
                          ).padStart(2, '0')} min`
                        : 'El plazo para este tipo de cambio ha caducado'}
                    </p>
                  )}
                  <p>
                    Tipo de cambio: S/{' '}
                    {operation?.action === OPERATION_KINDS_AUX.V ? currentBid : currentOffer}
                  </p>
                  {operation?.to_card && <p>{`Tipo de tarjeta: ${CARDS[operation?.to_card]}`}</p>}
                </div>
                {buildBaseInput(
                  `Ingresa el Monto a ${
                    operation?.action === OPERATION_KINDS_AUX.V ? 'Vender' : 'Comprar'
                  }`,
                  'text',
                  'amount',
                  {
                    ...REQUIRED_RULE,
                    maxLength: {
                      value: 12,
                      message: ERROR_MESSAGES.MAX_LENGTH(12),
                    },
                  },
                  {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      const fixed =
                        e.target.value.split('.').length > 1 &&
                        e.target.value.split('.')[1].length > 2
                          ? Number(e.target.value.replace(/,/g, '')).toFixed(2)
                          : e.target.value.replace(/,/g, '');
                      setValue('amount', fixed);
                      e.target.value = numberWithCommas(decimalOnly(fixed));
                      updateFromLeft(fixed, operation?.action);
                    },
                  }
                )}
                {buildBaseInput(
                  operation?.action === OPERATION_KINDS_AUX.V
                    ? 'Importe a recibir (Soles)'
                    : 'Importe que debes transferirnos (SOLES)',
                  'text',
                  'amount_aux',
                  {
                    ...REQUIRED_RULE,
                    maxLength: {
                      value: 12,
                      message: ERROR_MESSAGES.MAX_LENGTH(12),
                    },
                  },
                  {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      const fixed =
                        e.target.value.split('.').length > 1 &&
                        e.target.value.split('.')[1].length > 2
                          ? Number(e.target.value.replace(/,/g, '')).toFixed(2)
                          : e.target.value.replace(/,/g, '');
                      setValue('amount_aux', fixed);
                      e.target.value = numberWithCommas(decimalOnly(fixed));
                      updateFromRight(fixed, operation?.action, operation?.ranges);
                    },
                  }
                )}
                {buildBaseInput(
                  'Código Promo (Ingrésalo si tienes uno)',
                  'text',
                  'promoCode',
                  {
                    maxLength: {
                      value: 20,
                      message: ERROR_MESSAGES.MAX_LENGTH(20),
                    },
                  },
                  {}
                )}
                {buildBaseInput('Enviar constancia por', 'select', 'type', REQUIRED_RULE, {
                  options: [
                    {
                      value: 'mail',
                      name: 'Correo',
                    },
                    {
                      value: 'file',
                      name: 'Adjuntar Archivo',
                    },
                  ],
                })}
                {evidenceType === 'mail' ? (
                  <>
                    <span>Envíanos tu confirmacion de la transferencia a nuestro correo:</span>
                    <span className="ml-1 font-bold">cambios@{APP_NAME.toLowerCase()}.com</span>
                  </>
                ) : (
                  <div className="relative mx-auto max-w-lg rounded-md border-2 border-dashed border-gray-300 px-2 py-4 text-center">
                    <p className="mb-4">Enviar voucher o captura de la transferencia</p>
                    <div className="flex flex-col items-center justify-evenly sm:flex-row sm:space-y-0">
                      <div className="flex items-center space-x-2 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="text-sm text-gray-600">
                          <label
                            htmlFor="inp_document_file"
                            className="relative mx-auto cursor-pointer rounded-md bg-white font-medium text-primary-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 hover:text-primary-500"
                          >
                            <span>{documentFile ? 'Cambiar archivo' : 'Escoger archivo'}</span>
                            <input
                              id="inp_document_file"
                              name="inp_document_file"
                              type="file"
                              className="sr-only"
                              accept=".png, .jpg, .jpeg"
                              onChange={checkFile}
                            />
                          </label>
                          <p className="max-w-[120px] text-xs text-gray-500">imagen hasta 10MB</p>
                        </div>
                      </div>
                      {documentFile && imagePreview && (
                        <img
                          src={imagePreview as string}
                          className="max-h-64 max-w-full rounded-lg border-2 border-primary-200 sm:max-w-[50%]"
                          alt="Imagen del documento"
                        ></img>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-4 px-8 pt-4 pb-8">
                <h4 className="text-xl font-bold">Información de Abono para el cliente:</h4>
                <strong>Titular de la cuenta:</strong>
                <br />
                <span className="ml-2">{APP_NAME.toUpperCase()}</span>
                <div>
                  <span className="font-bold">
                    Banco{' '}
                    {!['BBVA', 'IBK'].includes(operation?.origin_bank || '')
                      ? 'BCP'
                      : operation?.origin_bank}
                    :
                  </span>
                  <br />
                  <span className="ml-2">
                    {operation?.is_origin_interbank ||
                    !['BBVA', 'IBK', 'BCP'].includes(operation?.origin_bank || '')
                      ? 'CCI'
                      : 'Cta. Cte.'}
                    {operation?.action === OPERATION_KINDS_AUX.V ? ' Dólares: ' : ' Soles: '}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-center bg-gray-50 py-4 px-8 sm:rounded-b-lg">
              <SolidButton color={BUTTON_COLORS.BLUE} className="w-auto px-12" isLoading={loading}>
                Confirmar
              </SolidButton>
            </div>
          </form>
        </div>
      </div>
    </PrivateRoute>
  );
}
